import React from 'react';
import BasicSettingIcon from '../common/BasicSettingIcon';
import {
  DEFAULT_SECTION_BASE_EXCLUDE_COLORS,
  DEFAULT_SECTION_DEDICATED_COLORS,
  DEFAULT_SECTION_REGULAR_COLORS,
} from '../../constants/programColorPalettes';
import type { ClassScheduleRow } from '../../services/sectionLayoutService';
import { ANNUAL_WEEKDAY_KEYS, buildAnnualPeriodLabels } from '../../utils/annualTimetable';
import {
  AnnualTimetablePreviewMainTable,
  createAnnualShellWeekRows,
} from './AnnualTimetablePreviewMainTable';

export type AnnualTimetableClassShellPreviewProps = {
  academicYear: number;
  showSaturday: boolean;
  gradeId: string;
  classTitle: string;
  classGrades: number[];
  /** 1학기·2학기 블록 순서 바꾸기 */
  swapTermOrder?: boolean;
  /** 기준시수 조정 버튼 행 오른쪽(예: 팀장 다운로드) */
  headerTrailing?: React.ReactNode;
};

const ClassBlock: React.FC<{ title: string; grades: number[]; className?: string }> = ({
  title,
  grades,
  className,
}) => (
  <div
    className={'cp-grade-track-class-block' + (className ? ` ${className}` : '')}
    aria-label="클래스"
  >
    <span className="cp-grade-track-class-block-label">{title}</span>
    {grades.map((num) => (
      <span key={num} className="class-grade-chip-display">
        {num}
      </span>
    ))}
  </div>
);

/** 연간시간표 클래스 섹션 좌상단: 「클래스 단독」 pill + 제목 + 클래스 블럭 */
export const AnnualTimetableClassSectionTitle: React.FC<{
  classTitle: string;
  classGrades: number[];
  meta?: React.ReactNode;
}> = ({ classTitle, classGrades, meta }) => (
  <div className="cp-annual-timetable-section-class-row-left">
    <div className="cp-class-members-section-header-title-row cp-annual-timetable-section-title-row">
      <div className="card-summary pill cp-track-class-pill">클래스 단독</div>
      <h3 className="cp-annual-timetable-section-class-name">연간 시간표</h3>
      <ClassBlock
        title={classTitle}
        grades={classGrades}
        className="cp-annual-timetable-section-class-block"
      />
    </div>
    {meta}
  </div>
);

/** 클래스 1개분 연간시간표 비활성 미리보기(통합 보기·학기 미생성 등) */
export const AnnualTimetableClassShellPreview: React.FC<AnnualTimetableClassShellPreviewProps> = ({
  showSaturday,
  gradeId,
  classTitle,
  classGrades,
  swapTermOrder = false,
  headerTrailing,
}) => {
  const weekdayKeysToShow = React.useMemo(
    () => (showSaturday ? ANNUAL_WEEKDAY_KEYS : ANNUAL_WEEKDAY_KEYS.filter((w) => w.key !== 'sat')),
    [showSaturday]
  );
  const periodLabels = React.useMemo(() => buildAnnualPeriodLabels([]), []);
  const sectionRows = React.useMemo<ClassScheduleRow[]>(() => [], []);
  const shellWeeks = React.useMemo(() => createAnnualShellWeekRows(4, showSaturday), [showSaturday]);

  const termToolbar = (termKey: '1' | '2', termLabel: string) => (
    <div
      className="cp-program-progress-toolbar cp-annual-term-program-toolbar"
      role="toolbar"
      aria-label="교환·복사·붙여넣기·데이터 삭제"
    >
      <div className="cp-program-progress-toolbar-leading">
        <span
          className={
            'cp-annual-term-title-badge' +
            (termKey === '1' ? ' cp-annual-term-title-badge--term1' : ' cp-annual-term-title-badge--term2')
          }
        >
          {termLabel}
        </span>
        <button type="button" className="cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-annual-progress-browse-left" disabled>
          <span className="cp-btn-program-browse-nav-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 14 14" focusable="false">
              <path d="M8.1 2v5.5L10 5.6l1.6 1.6L7 11.8 2.4 7.2 4 5.6l1.9 1.9V2h2.2z" fill="currentColor" />
            </svg>
          </span>
          <span className="cp-btn-label-desktop">선택셀 연간진도표 탐색</span>
          <span className="cp-btn-label-mobile-icon" aria-hidden>
            탐색
          </span>
        </button>
        <button type="button" className="cp-btn-copy-paste cp-btn-program-browse-nav" disabled>
          <span className="cp-btn-label-desktop">오늘 학습내용 보기</span>
          <span className="cp-btn-label-mobile-icon" aria-hidden>
            오늘 학습내용
          </span>
        </button>
        <button type="button" className="cp-btn-copy-paste cp-btn-program-browse-nav" disabled>
          <span className="cp-btn-label-desktop">내일 학습내용 보기</span>
          <span className="cp-btn-label-mobile-icon" aria-hidden>
            내일 학습내용
          </span>
        </button>
      </div>
      <div className="cp-program-progress-toolbar-trailing cp-annual-term-title-inline-actions">
        <div className="cp-program-progress-toolbar-group" role="group" aria-label="교환 영역">
          <button type="button" className="cp-btn-copy-paste cp-btn-swap-region" disabled>
            <span className="cp-btn-label-desktop">교환영역 A</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              A
            </span>
          </button>
          <button type="button" className="cp-btn-copy-paste cp-btn-swap-region" disabled>
            <span className="cp-btn-label-desktop">교환영역 B</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              B
            </span>
          </button>
          <button type="button" className="cp-btn-copy-paste cp-btn-swap-region" disabled>
            <span className="cp-btn-label-desktop">영역 바꾸기</span>
            <span className="cp-btn-label-mobile-icon cp-btn-mobile-swap-icon" aria-hidden>
              <span>A</span>
              <svg viewBox="0 0 20 20" className="cp-btn-mobile-action-svg">
                <path d="M3 6h11.2l-2.4-2.3 1.1-1.1L17.1 7l-4.2 4.4-1.1-1.1L14.2 8H3z" fill="currentColor" />
                <path d="M17 14H5.8l2.4 2.3-1.1 1.1L2.9 13l4.2-4.4 1.1 1.1L5.8 12H17z" fill="currentColor" />
              </svg>
              <span>B</span>
            </span>
          </button>
        </div>
        <div className="cp-program-progress-toolbar-group" role="group" aria-label="클립보드">
          <button type="button" className="cp-btn-copy-paste" disabled>
            <span className="cp-btn-label-desktop">복사</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/copy2.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button type="button" className="cp-btn-copy-paste" disabled>
            <span className="cp-btn-label-desktop">잘라내기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/cut.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button type="button" className="cp-btn-copy-paste" disabled>
            <span className="cp-btn-label-desktop">붙여넣기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/paste.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button type="button" className="cp-btn-copy-paste" disabled>
            <span className="cp-btn-label-desktop">데이터 삭제</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/del.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  const colorToolbar = (termKey: '1' | '2') => (
    <div className="cp-annual-term-toolbar" role="toolbar">
      <div className="cp-weekly-schedule-color-palettes cp-annual-timetable-toolbar-palettes" aria-label="색상">
        <div className="cp-annual-table-slot-legend" role="group" aria-label="수업·활동·비수업 셀 적용">
          <div className="cp-annual-slot-legend-item">
            <button type="button" className="cp-annual-slot-legend-swatch cp-annual-slot-legend-swatch--class" disabled title="수업" aria-label="수업" />
            <span className="cp-annual-slot-legend-tooltip" role="tooltip">
              수업
            </span>
          </div>
          <div className="cp-annual-slot-legend-item">
            <button type="button" className="cp-annual-slot-legend-swatch cp-annual-slot-legend-swatch--activity" disabled title="활동" aria-label="활동" />
            <span className="cp-annual-slot-legend-tooltip" role="tooltip">
              활동
            </span>
          </div>
          <div className="cp-annual-slot-legend-item">
            <button type="button" className="cp-annual-slot-legend-swatch cp-annual-slot-legend-swatch--nonclass" disabled title="비수업" aria-label="비수업" />
            <span className="cp-annual-slot-legend-tooltip" role="tooltip">
              비수업
            </span>
          </div>
        </div>
        <div className="cp-base-timetable-dedicated-palette cp-weekly-schedule-color-group">
          <span className="cp-base-timetable-dedicated-label">고정(행사)</span>
          <button type="button" className="cp-dedicated-color-btn cp-dedicated-color-none-btn" disabled title="고정(행사) 색상없음" aria-label="고정(행사) 색상없음" />
          {DEFAULT_SECTION_REGULAR_COLORS.map((hex) => (
            <button
              key={`shell-reg-${termKey}-${hex}`}
              type="button"
              className="cp-dedicated-color-btn"
              disabled
              title={hex}
              aria-label={`고정(행사) ${hex}`}
            >
              <span className="cp-dedicated-color-btn-inner" style={{ backgroundColor: hex }} aria-hidden />
            </button>
          ))}
        </div>
        <div className="cp-base-timetable-dedicated-palette cp-weekly-schedule-color-group">
          <span className="cp-base-timetable-dedicated-label">전담</span>
          <button type="button" className="cp-dedicated-color-btn cp-dedicated-color-none-btn" disabled title="전담 색상없음" aria-label="전담 색상없음" />
          {DEFAULT_SECTION_DEDICATED_COLORS.map((hex) => (
            <button
              key={`shell-ded-${termKey}-${hex}`}
              type="button"
              className="cp-dedicated-color-btn"
              disabled
              title={hex}
              aria-label={`전담 ${hex}`}
            >
              <span className="cp-dedicated-color-btn-inner" style={{ backgroundColor: hex }} aria-hidden />
            </button>
          ))}
        </div>
        <div className="cp-base-timetable-dedicated-palette cp-weekly-schedule-color-group">
          <span className="cp-base-timetable-dedicated-label">라벨링 색상</span>
          <button type="button" className="cp-dedicated-color-btn cp-dedicated-color-none-btn" disabled title="라벨링 색상 없음" aria-label="라벨링 색상 없음" />
          {DEFAULT_SECTION_BASE_EXCLUDE_COLORS.map((hex) => (
            <button
              key={`shell-exc-${termKey}-${hex}`}
              type="button"
              className="cp-dedicated-color-btn"
              disabled
              title={hex}
              aria-label={`라벨링 ${hex}`}
            >
              <span className="cp-dedicated-color-btn-inner" style={{ backgroundColor: hex }} aria-hidden />
            </button>
          ))}
        </div>
        <div className="cp-base-timetable-dedicated-palette cp-weekly-schedule-color-group cp-annual-project-palette">
          <span className="cp-base-timetable-dedicated-label">프로젝트</span>
          <button
            type="button"
            className="cp-dedicated-color-btn cp-annual-project-manage-gear-btn"
            disabled
            title="프로젝트 관리"
            aria-label="프로젝트 관리"
          >
            <BasicSettingIcon />
          </button>
          <button
            type="button"
            className="cp-dedicated-color-btn cp-dedicated-color-none-btn"
            disabled
            title="프로젝트 색상없음"
            aria-label="프로젝트 색상없음"
          />
          <button type="button" className="cp-dedicated-color-btn cp-dedicated-color-add-btn" disabled title="프로젝트 프로그램 생성" aria-label="프로젝트 프로그램 생성">
            +
          </button>
        </div>
      </div>
    </div>
  );

  const renderTermBlock = (termKey: '1' | '2', termLabel: string) => (
    <div
      key={termKey}
      id={`cp-annual-fixed-term-${gradeId}-${termKey}`}
      className="cp-annual-term-table-block"
    >
      {termToolbar(termKey, termLabel)}
      {colorToolbar(termKey)}
      <AnnualTimetablePreviewMainTable
        termKey={termKey}
        anchorGradeId={gradeId}
        weekdayKeysToShow={weekdayKeysToShow}
        periodLabels={periodLabels}
        sectionRows={sectionRows}
        weeks={shellWeeks}
        termHoursCell=""
      />
    </div>
  );

  return (
    <article
      className="cp-card cp-annual-timetable-per-class-card cp-annual-timetable-shell-preview cp-disabled-actual-ui"
    >
      <section className="cp-annual-timetable-class-section" aria-label={`${classTitle} 연간 시간표 미리보기`}>
        <div className="cp-annual-timetable-section-top">
          <div className="cp-annual-timetable-section-class-row">
            <AnnualTimetableClassSectionTitle classTitle={classTitle} classGrades={classGrades} />
            <div className="cp-annual-timetable-section-class-row-right">
              <div className="cp-annual-timetable-action-set cp-annual-timetable-action-set-base">
                <button
                  type="button"
                  className="cp-btn-secondary cp-annual-base-timetable-apply-btn cp-btn-annual-timetable-settings"
                  disabled
                >
                  기초 시간표 설정
                </button>
                <button type="button" className="cp-btn-secondary cp-annual-base-timetable-apply-btn" disabled>
                  1학기 기초시간표 반영
                </button>
                <button type="button" className="cp-btn-secondary cp-annual-base-timetable-apply-btn" disabled>
                  2학기 기초시간표 반영
                </button>
                <button
                  type="button"
                  className="cp-btn-secondary cp-annual-base-timetable-apply-btn cp-annual-hours-adjust-open-btn"
                  disabled
                >
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/MagicWand.svg`}
                    alt=""
                    className="cp-annual-hours-adjust-btn-icon"
                    width={14}
                    height={14}
                  />
                  기준시수 맞춤 시간표 자동 조정
                </button>
              </div>
              {headerTrailing}
            </div>
          </div>
        </div>
        <div className="cp-annual-timetable-table-panel">
          {swapTermOrder ? (
            <>
              {renderTermBlock('2', '2학기')}
              {renderTermBlock('1', '1학기')}
            </>
          ) : (
            <>
              {renderTermBlock('1', '1학기')}
              {renderTermBlock('2', '2학기')}
            </>
          )}
        </div>
      </section>
    </article>
  );
};

export type AnnualTimetableShellPreviewProps = {
  academicYear: number;
  showSaturday: boolean;
  swapTermOrder?: boolean;
};

/** 레거시 단일 빈 프레임(클래스 미연동 시) */
export const AnnualTimetableShellPreview: React.FC<AnnualTimetableShellPreviewProps> = ({
  academicYear,
  showSaturday,
  swapTermOrder,
}) => (
  <AnnualTimetableClassShellPreview
    academicYear={academicYear}
    showSaturday={showSaturday}
    swapTermOrder={swapTermOrder}
    gradeId="__annual_shell_preview__"
    classTitle="클래스 미연동"
    classGrades={[]}
  />
);
